import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useServicesStore } from '@/stores/servicesStore';
import { useServiceEnvironmentsStore } from '@/stores/serviceEnvironmentsStore';
import { usePermissions } from '@nimbitlabs/features';
import { useReadOnlyMode } from '@nimbitlabs/features';
import type { Environment } from '@/types/environment';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@nimbitlabs/ui';
import { 
  ArrowLeft, 
  Layers,
  Server, 
  Calendar, 
  Edit,
  Package,
  Lock
} from 'lucide-react';

export const EnvironmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getEnvironmentById } = useEnvironmentsStore();
  const { services: allServices } = useServicesStore();
  const { serviceEnvironments, fetchServiceEnvironments } = useServiceEnvironmentsStore();
  const { hasPermission } = usePermissions();
  const { isReadOnly } = useReadOnlyMode();
  
  const [environment, setEnvironment] = useState<Environment | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadEnvironment = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const env = await getEnvironmentById(id);
        setEnvironment(env as Environment);
        await fetchServiceEnvironments();
      } catch (e) {
        console.error('Failed to load environment:', e);
      } finally {
        setLoading(false);
      }
    };
    loadEnvironment();
  }, [id, getEnvironmentById, fetchServiceEnvironments]);

  // Filter services deployed to this environment
  const environmentServiceIds = serviceEnvironments
    .filter(se => se.environmentId === id)
    .map(se => se.serviceId);
  
  const environmentServices = allServices.filter(service => 
    environmentServiceIds.includes(service.id)
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500/20 text-green-600 border-green-500/40 dark:text-green-400';
      case 'Inactive':
        return 'bg-muted/30 text-muted-foreground border-muted/40';
      case 'Maintenance':
        return 'bg-orange-500/20 text-orange-600 border-orange-500/40 dark:text-orange-400';
      default:
        return 'bg-muted/30 text-muted-foreground border-muted/40';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/environments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Environments
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium mb-2">Loading environment...</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!environment) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/environments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Environments
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Environment not found</h3>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/environments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Environments
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{environment.name}</h1>
              {environment.isLocked && (
                <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                  <Lock className="h-3 w-3 mr-1" />
                  Locked
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{environment.description}</p>
          </div>
        </div>
        {hasPermission('environments:update') && !environment.isLocked && (
          <Button onClick={() => navigate(`/environments/${id}/edit`)} variant="outline" size="sm" disabled={isReadOnly}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Environment
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Type</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">{environment.type}</div>
            <p className="text-xs text-muted-foreground">Environment type</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge className={getStatusColor(environment.status)}>
              {environment.status}
            </Badge>
            <p className="text-xs text-muted-foreground mt-2">Current status</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Services</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{environmentServices.length}</div>
            <p className="text-xs text-muted-foreground">Deployed services</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Created</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{new Date(environment.createdAt).toLocaleDateString()}</div>
            <p className="text-xs text-muted-foreground">Creation date</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Environment Information */}
        <Card>
          <CardHeader>
            <CardTitle>Environment Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Type</label>
              <p className="text-sm mt-1 capitalize">{environment.type}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <div className="mt-1">
                <Badge className={getStatusColor(environment.status)}>
                  {environment.status}
                </Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="text-sm mt-1">{new Date(environment.createdAt).toLocaleDateString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
              <p className="text-sm mt-1">{new Date(environment.updatedAt).toLocaleDateString()}</p>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Services
            </CardTitle>
            <CardDescription>
              {environmentServices.length} {environmentServices.length === 1 ? 'service' : 'services'} deployed in this environment
            </CardDescription>
          </CardHeader>
          <CardContent>
            {environmentServices.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">No services deployed</p>
                <p className="text-xs mt-1">Services will appear here once deployed</p>
              </div>
            ) : (
              <div className="space-y-2">
                {environmentServices.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:border-primary/50 transition-colors cursor-pointer group"
                    onClick={() => navigate(`/services/${service.id}`)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{service.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{service.description}</p>
                    </div>
                    <Badge variant="outline" className="flex-shrink-0 text-xs capitalize">
                      {service.type}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
